import { useState } from "react";
import {
  Stack,
  Table,
  Button,
  Group,
  Text,
  Modal,
  TextInput,
  Box,
  Loader,
  Center,
  Alert,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconPlus,
  IconTrash,
  IconFingerprint,
  IconInfoCircle,
} from "@tabler/icons-react";
import {
  usePasskeys,
  useRegisterPasskey,
  useRemovePasskey,
} from "../../hooks/usePasskeys";

export default function PasskeyManager() {
  const { data: passkeys = [], isLoading, isError } = usePasskeys();
  const registerPasskey = useRegisterPasskey();
  const removePasskey = useRemovePasskey();

  const [registerOpened, { open: openRegister, close: closeRegister }] =
    useDisclosure(false);
  const [
    confirmRemoveOpened,
    { open: openConfirmRemove, close: closeConfirmRemove },
  ] = useDisclosure(false);

  const [deviceName, setDeviceName] = useState("");
  const [passkeyToRemove, setPasskeyToRemove] = useState<string | null>(null);

  const handleRegister = async () => {
    await registerPasskey.mutateAsync(deviceName || "My passkey");
    setDeviceName("");
    closeRegister();
  };

  const handleRemove = (id: string) => {
    setPasskeyToRemove(id);
    openConfirmRemove();
  };

  const confirmRemove = async () => {
    if (passkeyToRemove) {
      await removePasskey.mutateAsync(passkeyToRemove);
      closeConfirmRemove();
      setPasskeyToRemove(null);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "\u2014";
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <Center p="xl">
        <Loader />
      </Center>
    );
  }

  if (isError) {
    return (
      <Alert color="red" variant="light">
        Failed to load passkeys. Please try again.
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={500} size="lg">
          Passkeys
        </Text>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={openRegister}
          size="sm"
        >
          Register New Passkey
        </Button>
      </Group>

      <Text size="sm" c="dimmed">
        Passkeys let you sign in securely using your fingerprint, face, or
        device PIN instead of a password.
      </Text>

      {passkeys.length === 0 ? (
        <Stack align="center" gap="sm" py="xl">
          <IconFingerprint size={48} color="var(--mantine-color-dimmed)" />
          <Text c="dimmed" ta="center">
            No passkeys registered yet
          </Text>
          <Text size="sm" c="dimmed" ta="center">
            Register a passkey to enable passwordless sign-in.
          </Text>
        </Stack>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Device Name</Table.Th>
              <Table.Th>Registered</Table.Th>
              <Table.Th>Last Used</Table.Th>
              <Table.Th style={{ width: 80 }}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {passkeys.map((pk) => (
              <Table.Tr key={pk.id}>
                <Table.Td>
                  <Text size="sm" fw={500}>
                    {pk.deviceName || "Unnamed"}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{formatDate(pk.registeredAt)}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{formatDate(pk.lastUsedAt)}</Text>
                </Table.Td>
                <Table.Td>
                  <Button
                    variant="subtle"
                    color="red"
                    size="compact-sm"
                    leftSection={<IconTrash size={14} />}
                    onClick={() => handleRemove(pk.id)}
                    loading={removePasskey.isPending}
                  >
                    Remove
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {/* Register Passkey Modal */}
      <Modal
        opened={registerOpened}
        onClose={closeRegister}
        title="Register New Passkey"
        size="sm"
        styles={{ body: { padding: 0 } }}
      >
        <Stack gap="md" px="md" pt="md">
          <Alert
            icon={<IconInfoCircle size={16} />}
            color="blue"
            variant="light"
          >
            You will be prompted by your browser to create a passkey using your
            fingerprint, face, or device PIN.
          </Alert>
          <TextInput
            label="Device Name"
            placeholder='e.g. "MacBook Pro", "iPhone"'
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
          />
        </Stack>
        <Box
          px="md"
          py="sm"
          mt="md"
          style={{
            borderTop: "1px solid var(--mantine-color-default-border)",
          }}
        >
          <Group justify="space-between">
            <Button variant="default" onClick={closeRegister}>
              Cancel
            </Button>
            <Button
              onClick={handleRegister}
              loading={registerPasskey.isPending}
              leftSection={<IconFingerprint size={16} />}
            >
              Register Passkey
            </Button>
          </Group>
        </Box>
      </Modal>

      {/* Confirm Remove Modal */}
      <Modal
        opened={confirmRemoveOpened}
        onClose={closeConfirmRemove}
        title="Remove Passkey"
        size="sm"
        centered
        styles={{ body: { padding: 0 } }}
      >
        <Stack gap="md" px="md" pt="md">
          <Text>
            Are you sure you want to remove this passkey? You will no longer be
            able to use it to sign in.
          </Text>
        </Stack>
        <Box
          px="md"
          py="sm"
          mt="md"
          style={{
            borderTop: "1px solid var(--mantine-color-default-border)",
          }}
        >
          <Group justify="space-between">
            <Button variant="default" onClick={closeConfirmRemove}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={confirmRemove}
              loading={removePasskey.isPending}
            >
              Remove Passkey
            </Button>
          </Group>
        </Box>
      </Modal>
    </Stack>
  );
}
